import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Navbar } from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { KEY_MOMENTS } from "@/pages/Landing";
import { Check, ShieldCheck } from "lucide-react";

const BG_TENANT = "https://images.unsplash.com/photo-1758523671826-d7f8217ffac3?crop=entropy&cs=srgb&fm=jpg&q=80&w=1600";

export default function TenantPricing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [promo, setPromo] = useState(null);

  useEffect(() => { api.get("/promo/status").then(({ data }) => setPromo(data)).catch(() => {}); }, []);

  const plans = [
    {
      id: "verification",
      name: "Verification",
      price: 24.99,
      unit: "one-time",
      cta: "Get verified",
      features: ["Background + credit check", "Income & employment verification", "ID + selfie verification", "Verified badge — enter the offer pool"],
    },
    {
      id: "reactivation",
      name: "Reactivation",
      price: 10,
      unit: "when you return",
      popular: true,
      cta: "How it works",
      features: ["Fresh credit check", "Verified badge refreshes with new date", "Profile goes live again", "Only pay when you re-enter the pool"],
    },
    {
      id: "boost",
      name: "Featured Boost",
      price: 19,
      unit: "per boost",
      cta: "Boost my profile",
      features: ["Top placement in landlord browse", "More profile views", "Stand out from other tenants", "Optional — never required"],
    },
  ];

  const go = () => {
    if (!user) { localStorage.setItem("rm_pending_role", "tenant"); navigate("/auth?role=tenant"); return; }
    if (user.role === "tenant") { navigate("/tenant"); return; }
    navigate("/auth?role=tenant");
  };

  return (
    <div className="relative min-h-screen overflow-hidden">
      <div aria-hidden className="absolute inset-0 z-0">
        <img src={BG_TENANT} alt="" className="w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-background/85" />
      </div>
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 md:px-8 py-16 relative z-10">
        <div className="text-center mb-6">
          <img src={KEY_MOMENTS[0].url} alt={KEY_MOMENTS[0].alt} className="w-20 h-20 rounded-full object-cover mx-auto mb-4 border-4 border-white shadow-sm" />
          <p className="overline text-primary">Tenant plans</p>
          <h1 className="font-display text-4xl sm:text-5xl tracking-tighter font-bold mt-2">Verify once. Get pitched.</h1>
          <p className="text-muted-foreground mt-3">No monthly fees for tenants — pay once to get verified, then receive offers.</p>
        </div>

        {promo?.active && (
          <div data-testid="tenant-promo" className="max-w-2xl mx-auto mb-10 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-sm p-4 text-center text-sm font-semibold flex items-center justify-center gap-2">
            <ShieldCheck className="w-4 h-4" />
            First 100 tenants verify FREE — only {promo.spots_left} spots left!
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
          {plans.map((p) => {
            const isFreeVerify = p.id === "verification" && promo?.active;
            return (
              <div key={p.id} data-testid={`tenant-plan-${p.id}`} className={`bg-white border rounded-sm p-8 flex flex-col ${p.popular ? "border-primary ring-1 ring-primary" : "border-border"}`}>
                {p.popular && <span className="overline text-primary mb-2">Most common</span>}
                {isFreeVerify && <span className="overline text-emerald-600 mb-2">Free for first 100</span>}
                <h3 className="font-display text-2xl font-semibold">{p.name}</h3>
                <div className="my-4">
                  {isFreeVerify ? (
                    <>
                      <span className="font-display text-4xl font-bold text-emerald-600">FREE</span>
                      <span className="text-muted-foreground line-through ml-2">${p.price}</span>
                    </>
                  ) : (
                    <>
                      <span className="font-display text-4xl font-bold">${p.price}</span>
                      <span className="text-muted-foreground"> · {p.unit}</span>
                    </>
                  )}
                </div>
                <ul className="space-y-2 mb-8 flex-1">
                  {p.features.map((f) => (<li key={f} className="flex items-center gap-2 text-sm"><Check className="w-4 h-4 text-emerald-600" />{f}</li>))}
                </ul>
                <Button data-testid={`tenant-choose-${p.id}`} variant={p.popular ? "default" : "outline"} onClick={go}>{p.cta}</Button>
              </div>
            );
          })}
        </div>

        <div className="text-center mt-12 text-sm text-muted-foreground">
          Are you a landlord? <button className="text-primary underline underline-offset-2" data-testid="link-landlord-pricing" onClick={() => navigate("/pricing")}>See landlord plans →</button>
        </div>
      </div>
    </div>
  );
}
